import { Dialog, Form, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { TbDeviceFloppy } from "react-icons/tb";
import {
  EditPurchaseBodySchema,
  MORTGAGE_TYPES,
  type Purchase,
  toBigNumber,
} from "shared";
import { Api } from "@/api.config";
import { Button } from "@/components/Button";
import { FormGroup } from "@/components/form/FormGroup";
import { NumberInput } from "@/components/form/NumberInput";
import { PriceInput, Transformers } from "@/components/form/PriceInput";
import { TextareaField } from "@/components/form/TextareaField";
import { TextField } from "@/components/form/TextField";
import { QueryKeys } from "@/LinkGen";

interface EditPurchaseDialogProps {
  purchase: Purchase;
}

export const EditPurchaseDialog = (props: EditPurchaseDialogProps) => {
  const { purchase } = props;
  const dialog = useDialog();
  const queryClient = useQueryClient();

  const isTBD = purchase.addressID === null;

  const { handleSubmit, formState, control, register, watch } = useForm({
    resolver: zodResolver(EditPurchaseBodySchema),
    reValidateMode: "onBlur",
    defaultValues: {
      mortgageType: purchase.mortgageType as (typeof MORTGAGE_TYPES)[number],
      price: purchase.price,
      downpayment: purchase.downpayment,
      earnest: purchase.earnest,
      sellerCredit: purchase.sellerCredit,
      fundingDate: purchase.fundingDate,
      annualInsurance: purchase.annualInsurance,
      annualHOA: purchase.annualHOA,
      annualTax:
        purchase.annualTax == null ? null : new BigNumber(purchase.annualTax),
      addressID: purchase.addressID,
      vaFirstTime: purchase.vaFirstTime,
      vaExempt: purchase.vaExempt,
      note: purchase.note,
    },
  });

  const mortgageType = watch("mortgageType");
  const priceRaw = watch("price") as string;
  const price = useMemo(() => toBigNumber(priceRaw), [priceRaw]);

  const annualModes = useMemo(
    () => ({
      annual: {
        dp: 2,
        label: "Yr",
        transformer: Transformers.Base(),
        tooltip: "Per year",
      },
      monthly: {
        dp: 2,
        label: "Mo",
        transformer: Transformers.Fraction(12),
        tooltip: "Per month",
      },
      percentage: {
        dp: 2,
        label: "%",
        transformer: Transformers.PercentageOf(price),
        tooltip: "Percentage of the purchase price per year",
      },
    }),
    [price],
  );

  const downpaymentModes = useMemo(
    () => ({
      flat: {
        dp: 2,
        label: "$",
        transformer: Transformers.Base(),
        tooltip: "Flat amount",
      },
      percentage: {
        dp: 2,
        label: "%",
        transformer: Transformers.PercentageOf(price),
        tooltip: "Percentage of the purchase price",
      },
    }),
    [price],
  );

  const onSubmit = handleSubmit(async (values) => {
    await Api.PurchaseEdit(purchase.ID, values);
    await queryClient.refetchQueries({
      queryKey: QueryKeys.Purchase(purchase.ID),
    });
    dialog.close();
  });

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Edit purchase</Dialog.Header>
      <Dialog.Body>
        <FormGroup
          label="Mortgage type"
          htmlFor="mortgageType"
          required
          error={formState.errors.mortgageType?.message}
        >
          <Form.Select
            id="mortgageType"
            error={!!formState.errors.mortgageType}
            {...register("mortgageType")}
          >
            <option value="">Select type...</option>
            {MORTGAGE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Form.Select>
        </FormGroup>
        {mortgageType === "VA" && (
          <>
            <FormGroup
              label="VA first time use"
              htmlFor="vaFirstTime"
              required
              error={formState.errors.vaFirstTime?.message}
            >
              <Form.Select
                id="vaFirstTime"
                error={!!formState.errors.vaFirstTime}
                {...register("vaFirstTime", {
                  setValueAs: (value) =>
                    value === "" ? null : value === "true",
                })}
              >
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Form.Select>
            </FormGroup>
            <FormGroup
              label="Exempt (Disability rate 10%+)"
              htmlFor="vaExempt"
              required
              error={formState.errors.vaExempt?.message}
            >
              <Form.Select
                id="vaExempt"
                error={!!formState.errors.vaExempt}
                {...register("vaExempt", {
                  setValueAs: (value) =>
                    value === "" ? null : value === "true",
                })}
              >
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Form.Select>
            </FormGroup>
          </>
        )}
        <NumberInput
          label="Price"
          required
          placeholder="Purchase price"
          id="price"
          control={control}
          name="price"
        />
        <FormGroup
          label="Downpayment"
          htmlFor="downpayment"
          required
          error={formState.errors.downpayment?.message}
        >
          <PriceInput
            placeholder="Downpayment amount"
            id="downpayment"
            control={control}
            name="downpayment"
            modes={downpaymentModes}
            defaultMode="flat"
            summaryLabel="Amount"
          />
        </FormGroup>
        <NumberInput
          label="Earnest"
          required
          placeholder="Earnest money"
          id="earnest"
          control={control}
          name="earnest"
        />
        <NumberInput
          label="Seller credit"
          required
          placeholder="Seller credit"
          id="sellerCredit"
          control={control}
          name="sellerCredit"
        />
        <TextField
          control={control}
          name="fundingDate"
          label="Funding date"
          type="date"
          required
        />
        <FormGroup
          label="Property insurance"
          htmlFor="annualInsurance"
          required
          error={formState.errors.annualInsurance?.message}
        >
          <PriceInput
            placeholder="Insurance amount"
            id="annualInsurance"
            control={control}
            name="annualInsurance"
            modes={annualModes}
            defaultMode="monthly"
          />
        </FormGroup>
        <FormGroup
          label="HOA fee"
          htmlFor="annualHOA"
          required
          error={formState.errors.annualHOA?.message}
        >
          <PriceInput
            placeholder="HOA amount"
            id="annualHOA"
            control={control}
            name="annualHOA"
            modes={annualModes}
            defaultMode="monthly"
          />
        </FormGroup>
        {isTBD && (
          <FormGroup
            label="Annual tax"
            htmlFor="annualTax"
            required
            error={formState.errors.annualTax?.message}
          >
            <PriceInput
              placeholder="Tax amount"
              id="annualTax"
              control={control}
              name="annualTax"
              modes={annualModes}
              defaultMode="percentage"
              defaultTyped={0.6}
            />
          </FormGroup>
        )}
        <TextareaField
          control={control}
          name="note"
          label="Note"
          placeholder="Optional note"
        />
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
